import localesApi from "../api/LocalesApi";
import { FormattedLocales } from "../types/locales";
import { getLocalizationFormat, hasLocalesToFetch } from "../utils/easyLocale";
import { getItemFromSessionStorage, setItemToSessionStorage } from "../utils/sessionStorage";
import { SESSION_STORAGE_LANGUAGE_KEY, SESSION_STORAGE_LOCALES_KEY } from "../constants/easyLocales";
import { computed, ref } from "vue";

export class EasyLocale {
  private loadingIndex = ref<number | null>(null);
  private locales = ref<FormattedLocales>({});
  private loadedLocalizationKeys = ref<string[]>([]);

  public loaded = computed(() => {
    return this.loadingIndex.value === 0;
  });

  get getLocales(): FormattedLocales {
    return this.locales.value;
  }

  public getLocale = (key: string | null | undefined, substitutions?: Record<string, string | number>): string => {
    if (!this.isLocalizationKeyValid(key)) return "";

    const value = this.localizeWithSubstitutions(key as string, substitutions);
    if (value == undefined) {
      console.error(`Localization '${key}' not found. Add key to keys file`);
      return key as string;
    }

    return value;
  };

  isLocalizationKeyValid(key: string | null | undefined): boolean {
    if (!key) {
      console.error("Localization key is missing");
      return false;
    }

    const value = this.locales.value[key];
    if (!value && !this.loaded.value) {
      return false;
    }

    return true;
  }

  localizeWithSubstitutions(key: string, substitutions?: Record<string, string | number>) {
    let value = this.locales.value[key];
    if (substitutions && value) {
      for (const subst in substitutions) {
        value = value.replace(getLocalizationFormat(subst), String(substitutions[subst]));
      }
    }
    return value;
  }

  async fetchLocales(localizationKeys: string[] | null | undefined) {
    this.initSessionLocales();

    if (!hasLocalesToFetch(localizationKeys)) return;

    const uniqueLocalizationKeys = this.getUniqueLocalizationKeys(localizationKeys as string[]);

    if (uniqueLocalizationKeys.length === 0) {
      this.loadingIndex.value = 0;
      return;
    }
    this.addLoadedLocalizationKeys(uniqueLocalizationKeys);
    await this.fetchUniqueLocales(uniqueLocalizationKeys);
  }

  getUniqueLocalizationKeys(localizationKeys: string[]): string[] {
    return localizationKeys.filter(key => !this.loadedLocalizationKeys.value.includes(key));
  }

  addLoadedLocalizationKeys(localizationKeys: string[]) {
    this.loadedLocalizationKeys.value = this.loadedLocalizationKeys.value.concat(localizationKeys);
  }

  initSessionLocales() {
    if (this.hasStoredLanguageChanged()) {
      this.resetLocales();
    } else {
      this.setLocalesFromSessionStorage();
    }
  }

  private hasStoredLanguageChanged(): boolean {
    const storedLanguage = getItemFromSessionStorage(SESSION_STORAGE_LANGUAGE_KEY);
    const currentLanguage = window.EASY.currentUser.language;

    if (!storedLanguage || !currentLanguage) return true;

    return storedLanguage !== currentLanguage;
  }

  resetLocales(): void {
    this.loadedLocalizationKeys.value = [];
    this.locales.value = {};
    this.clearLocalesSessionStorage();
  }

  private setLocalesFromSessionStorage(): void {
    const storedLocales = getItemFromSessionStorage(SESSION_STORAGE_LOCALES_KEY);
    if (!storedLocales) return;
    this.loadedLocalizationKeys.value = Object.keys(storedLocales);
    this.locales.value = storedLocales;
  }

  clearLocalesSessionStorage() {
    sessionStorage.removeItem(SESSION_STORAGE_LOCALES_KEY);
    sessionStorage.removeItem(SESSION_STORAGE_LANGUAGE_KEY);
  }

  async fetchUniqueLocales(uniqueLocalizationKeys: string[]) {
    const loadingAction: Promise<FormattedLocales> = localesApi.getLocales(uniqueLocalizationKeys);
    this.loadingIndex.value! += 1;
    const fetchedLocales = await loadingAction;
    this.locales.value = { ...fetchedLocales, ...this.locales.value };
    this.loadingIndex.value! -= 1;

    this.saveLocalesToSessionStorage();
  }

  private saveLocalesToSessionStorage(): void {
    setItemToSessionStorage(SESSION_STORAGE_LOCALES_KEY, this.locales.value);
    setItemToSessionStorage(SESSION_STORAGE_LANGUAGE_KEY, window.EASY?.currentUser?.language);
  }
}

export const easyLocale = new EasyLocale();
